import React, { useState, useEffect, useMemo } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";
import LoginPage from "./Authentication/Login";
import LoginConsole from "./Loginconsole";
import ErrorBoundary from "./Boundary";

// MUI imports
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import { Menu, Close } from "@mui/icons-material";

// Framer Motion
import { motion, AnimatePresence } from "framer-motion";

export default function Dashboard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState("dashboard");

  // Track Firebase auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: "light",
          primary: { main: "#4f46e5" },
          background: { default: "#f9fafb", paper: "#fff" },
        },
        shape: { borderRadius: 16 },
      }),
    []
  );

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (err) {
      console.error("Sign-out error:", err);
    }
  };

  if (!user) return <LoginPage />;

  // Content rendering based on active page
  const renderContent = () => {
    switch (activePage) {
      case "dashboard":
        return (
          <ErrorBoundary>
            <LoginConsole />
          </ErrorBoundary>
        );
      case "routes":
      case "analytics":
      case "settings":
        return (
          <ErrorBoundary>
            <Typography>⚙️ This Page Content is Under Construction. See you Soon!</Typography>
          </ErrorBoundary>
        );
      default:
        return <Typography>Welcome to ContainerKing</Typography>;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
        {/* Sidebar */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              style={{ width: 240, zIndex: 1200 }}
            >
              <Paper elevation={6} sx={{ height: "100%", p: 2 }}>
                <List>
                  {["dashboard", "routes", "analytics", "settings"].map((page) => (
                    <ListItemButton
                      key={page}
                      selected={activePage === page}
                      onClick={() => {
                        setActivePage(page);
                        setIsSidebarOpen(false);
                      }}
                    >
                      <ListItemText primary={page.charAt(0).toUpperCase() + page.slice(1)} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Overlay */}
        {isSidebarOpen && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={toggleSidebar}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              bgcolor: "black",
              zIndex: 1100,
            }}
          />
        )}

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Navbar */}
          <Paper
            elevation={3}
            sx={{
              p: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: 1000,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                edge="start"
                onClick={toggleSidebar}
                sx={{ mr: 1, display: { lg: "none" } }}
              >
                {isSidebarOpen ? <Close /> : <Menu />}
              </IconButton>
              <Typography variant="h6">ContainerKing</Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ mr: 2 }}>{user?.email}</Typography>
              <Button variant="contained" color="primary" onClick={handleSignOut}>
                Sign Out
              </Button>
            </Box>
          </Paper>

          {/* Main Content */}
          <Box sx={{ p: 3, flex: 1 }}>
            <motion.div
              key={activePage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
                <Typography variant="h5" sx={{ mb: 3 }}>
                  Harisha's Control Panel
                </Typography>
                {renderContent()}
              </Paper>
            </motion.div>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
